import { useState, useMemo, useRef, useEffect } from 'react'
import {
  ProductCategories,
  ProductCategoryLabels,
  type MasterProduct,
} from '@esf/shared'

// Simple icon renderer
const renderIcon = (path: string, className = 'w-5 h-5') => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
  </svg>
)

// Category badge component
const CategoryBadge = ({ category }: { category: string }) => {
  const colors: Record<string, string> = {
    seating: 'bg-blue-100 text-blue-700',
    litter_recycling: 'bg-green-100 text-green-700',
    shelters: 'bg-purple-100 text-purple-700',
    lighting: 'bg-yellow-100 text-yellow-700',
    signage: 'bg-orange-100 text-orange-700',
    planters: 'bg-emerald-100 text-emerald-700',
    bollards_barriers: 'bg-red-100 text-red-700',
    cycle_parking: 'bg-cyan-100 text-cyan-700',
    ev_charging: 'bg-indigo-100 text-indigo-700',
    digital_displays: 'bg-pink-100 text-pink-700',
  }
  return (
    <span className={`badge ${colors[category] || 'bg-gray-100 text-gray-700'}`}>
      {ProductCategoryLabels[category as keyof typeof ProductCategoryLabels] || category}
    </span>
  )
}

interface DppTemplatesPageProps {
  masterProducts: MasterProduct[]
  navigateTo: (page: string, id?: string) => void
  setSelectedMasterProduct: (mp: MasterProduct) => void
  setMasterProductDetailModalOpen: (v: boolean) => void
  fetchMasterProducts: () => void
  showDeleted: boolean
  setShowDeleted: (v: boolean) => void
  onRestoreMasterProduct: (mp: MasterProduct) => void
}

export default function DppTemplatesPage({
  masterProducts,
  navigateTo,
  setSelectedMasterProduct,
  setMasterProductDetailModalOpen,
  fetchMasterProducts,
  showDeleted,
  setShowDeleted,
  onRestoreMasterProduct,
}: DppTemplatesPageProps) {
  const [mpCategoryFilter, setMpCategoryFilter] = useState('')
  const [mpActiveFilter, setMpActiveFilter] = useState('')

  // Local search state for immediate feedback
  const [searchInput, setSearchInput] = useState('')
  const [mpSearchQuery, setMpSearchQuery] = useState('')
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    searchTimeoutRef.current = setTimeout(() => {
      setMpSearchQuery(value)
    }, 300)
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  const filteredMasterProducts = useMemo(() => {
    let filtered = masterProducts
    if (mpSearchQuery) {
      const q = mpSearchQuery.toLowerCase()
      filtered = filtered.filter(mp =>
        mp.name?.toLowerCase().includes(q) ||
        mp.model?.toLowerCase().includes(q) ||
        mp.sku?.toLowerCase().includes(q)
      )
    }
    if (mpCategoryFilter) {
      filtered = filtered.filter(mp => mp.category === mpCategoryFilter)
    }
    if (mpActiveFilter !== '') {
      filtered = filtered.filter(mp => mp.isActive === (mpActiveFilter === 'true'))
    }
    return filtered
  }, [masterProducts, mpSearchQuery, mpCategoryFilter, mpActiveFilter])

  const clearFilters = () => {
    setSearchInput('')
    setMpSearchQuery('')
    setMpCategoryFilter('')
    setMpActiveFilter('')
    setShowDeleted(false)
  }

  return (
    <div className="container-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">DPP Templates</h1>
          <p className="text-sm text-gray-500 mt-1">Digital Product Passport templates for creating new assets with pre-populated data</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => navigateTo('dpp-template-create')}
        >
          {renderIcon('M12 4v16m8-8H4')}
          Create Template
        </button>
      </div>

      {/* Filters */}
      <div className="filter-bar">
        <input
          type="text"
          placeholder="Search templates..."
          className="filter-input flex-1"
          value={searchInput}
          onChange={e => handleSearchChange(e.target.value)}
        />
        <select
          className="filter-select"
          value={mpCategoryFilter}
          onChange={e => setMpCategoryFilter(e.target.value)}
        >
          <option value="">All Categories</option>
          {ProductCategories.map(c => <option key={c} value={c}>{ProductCategoryLabels[c]}</option>)}
        </select>
        <select
          className="filter-select"
          value={mpActiveFilter}
          onChange={e => setMpActiveFilter(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={showDeleted}
            onChange={(e) => setShowDeleted(e.target.checked)}
            className="rounded border-gray-300"
          />
          Show deleted
        </label>
        <button onClick={fetchMasterProducts} className="btn btn-secondary">
          {renderIcon('M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15')}
        </button>
        {(searchInput || mpCategoryFilter || mpActiveFilter || showDeleted) && (
          <button onClick={clearFilters} className="btn btn-outline text-sm">
            Clear filters
          </button>
        )}
      </div>

      {/* DPP Templates Table */}
      <div className="table-card">
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Category</th>
              <th>Model</th>
              <th>SKU</th>
              <th>Manufacturer</th>
              <th>Status</th>
              <th>Version</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredMasterProducts.map(mp => (
              <tr key={mp.id} className={mp.deletedAt ? 'opacity-50 bg-gray-50' : ''}>
                <td className="font-medium">{mp.name}</td>
                <td><CategoryBadge category={mp.category} /></td>
                <td className="text-sm text-gray-600">{mp.model || '-'}</td>
                <td className="font-mono text-sm text-gray-500">{mp.sku || '-'}</td>
                <td className="text-sm text-gray-600">{mp.manufacturer || '-'}</td>
                <td>
                  {mp.deletedAt ? (
                    <span className="badge bg-red-100 text-red-700">Deleted</span>
                  ) : (
                    <span className={`badge ${mp.isActive ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-500'}`}>
                      {mp.isActive ? 'Active' : 'Inactive'}
                    </span>
                  )}
                </td>
                <td className="text-sm text-gray-500">v{mp.version}</td>
                <td>
                  <div className="flex gap-1">
                    <button
                      className="btn btn-sm btn-outline"
                      onClick={() => {
                        setSelectedMasterProduct(mp)
                        setMasterProductDetailModalOpen(true)
                      }}
                    >
                      View
                    </button>
                    {mp.deletedAt ? (
                      <button
                        className="btn btn-sm bg-success-50 text-success-600 hover:bg-success-100"
                        onClick={() => onRestoreMasterProduct(mp)}
                        title="Restore template"
                      >
                        Restore
                      </button>
                    ) : (
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => navigateTo('dpp-template-edit', mp.id)}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filteredMasterProducts.length === 0 && (
              <tr>
                <td colSpan={8}>
                  <div className="empty-state">
                    <div className="empty-state-icon">
                      {renderIcon('M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z')}
                    </div>
                    <div className="empty-state-title">No DPP templates found</div>
                    <div className="empty-state-text">
                      {mpSearchQuery || mpCategoryFilter || mpActiveFilter
                        ? 'Try adjusting your filters'
                        : 'Create a DPP template to streamline asset creation'}
                    </div>
                    {!mpSearchQuery && !mpCategoryFilter && !mpActiveFilter && (
                      <button
                        className="btn btn-primary mt-4"
                        onClick={() => navigateTo('dpp-template-create')}
                      >
                        {renderIcon('M12 4v16m8-8H4')}
                        Create Template
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
